import { Component } from '@theme/component';
import { prefersReducedMotion } from '@theme/utilities';

/**
 * A floating button that returns the visitor to the top of the page.
 * It becomes visible once the page is scrolled past one viewport height.
 *
 * @typedef {object} Refs
 * @property {HTMLAnchorElement} button - The link that triggers the scroll.
 *
 * @extends {Component<Refs>}
 */
class BackToTopComponent extends Component {
  requiredRefs = ['button'];

  /** @type {AbortController | undefined} */
  #abortController;

  #frame = 0;

  connectedCallback() {
    super.connectedCallback();

    this.#abortController?.abort();
    this.#abortController = new AbortController();
    window.addEventListener('scroll', this.#handleScroll, { passive: true, signal: this.#abortController.signal });
    this.#updateVisibility();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController?.abort();
    cancelAnimationFrame(this.#frame);
    this.#frame = 0;
  }

  /**
   * Scrolls to the top and moves focus to the start of the page.
   * @param {MouseEvent} event
   */
  scrollToTop(event) {
    event.preventDefault();

    window.scrollTo({ top: 0, behavior: prefersReducedMotion() ? 'auto' : 'smooth' });

    // Keyboard users continue from the top instead of the button's old position.
    const skipLink = document.querySelector('.skip-to-content-link');
    if (skipLink instanceof HTMLElement) skipLink.focus({ preventScroll: true });
  }

  #handleScroll = () => {
    if (this.#frame) return;

    this.#frame = requestAnimationFrame(() => {
      this.#frame = 0;
      this.#updateVisibility();
    });
  };

  #updateVisibility() {
    this.toggleAttribute('data-visible', window.scrollY > window.innerHeight);
  }
}

if (!customElements.get('back-to-top-component')) {
  customElements.define('back-to-top-component', BackToTopComponent);
}
